import { Lock } from "lucide-react";
import { useEffect, useRef } from "react";
import { Control, useFormState } from "react-hook-form";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSearchSelect } from "../../../../components/ui/Input/FormSearchSelect/FormSearchSelect";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import { RECIPE_INGREDIENT_TYPE } from "../../../../config/optionsConstants";
import { errorView } from "../../../../helpers/ErrorHandler";
import { canRemoveRow, isRowSaveable, validatePercentage } from "../helpers/recipeAddIngredient.helpers";
import {
  CATEGORY_LABELS,
  GenericIngredientOption,
  IngredientDraftFormValues,
  IngredientDraftRow as IngredientDraftRowData,
  ProductUnitOption
} from "../types/recipeAddIngredient.types";
import GenericDatasetDescription from "./GenericDatasetDescription";
import IngredientDraftActions from "./IngredientDraftActions";

interface IngredientDraftRowProps {
  index: number;
  draft: IngredientDraftRowData;
  control: Control<IngredientDraftFormValues>;
  productUnitOptions: ProductUnitOption[];
  genericOptions: GenericIngredientOption[];
  pendingCount: number;
  hasSaved: boolean;
  isSaving: boolean;
  onIngredientTypeChange: (index: number, value: string) => void;
  onPercentageChange: (index: number, value: string) => void;
  onDescriptionOpenChange: (index: number, open: boolean) => void;
  onSave: (index: number) => void;
  onSaveAndAddNew: (index: number) => void;
  onRemove: (index: number) => void;
}

const ingredientTypeOptions = RECIPE_INGREDIENT_TYPE.map(option => ({
  key: option.key,
  value: option.value,
  label: option.text
}));

const IngredientDraftRow = ({
  index,
  draft,
  control,
  productUnitOptions,
  genericOptions,
  pendingCount,
  hasSaved,
  isSaving,
  onIngredientTypeChange,
  onPercentageChange,
  onDescriptionOpenChange,
  onSave,
  onSaveAndAddNew,
  onRemove
}: IngredientDraftRowProps) => {
  const isOwnProduct = draft.ingredientType === RecipeIngredientType.OWN_PRODUCT;
  const isGeneric = draft.ingredientType === RecipeIngredientType.GENERIC_INGREDIENT;
  const isExisting = Boolean(draft.isExisting);
  const isPercentageDisabled = draft.saved;

  const searchableProductOptions = productUnitOptions
    .filter(option => !option.disabled || option.value === draft.productUnitKey)
    .map(option => ({
      id: option.id,
      title: option.title,
      value: option.value,
      disabled: option.disabled
    }));

  const searchableGenericOptions = genericOptions
    .filter(option => !option.disabled || String(option.value) === String(draft.ingredientId))
    .map(option => ({
      id: option.id,
      title: option.title,
      value: option.value,
      disabled: option.disabled
    }));

  const ingredientLabel = draft.ingredientType
    ? CATEGORY_LABELS[draft.ingredientType as RecipeIngredientType]
    : "Ingredient";

  const categoryLabel = draft.ingredientType
    ? CATEGORY_LABELS[draft.ingredientType as RecipeIngredientType]
    : "Category";

  const selectedGeneric = genericOptions.find(
    option => String(option.value) === String(draft.ingredientId)
  );
  const selectedProductUnit = productUnitOptions.find(
    option => String(option.value) === String(draft.productUnitKey)
  );

  // Prefer live option titles so existing rows still show the correct name
  // when recipe payload omits / has a stale ingredientName.
  const resolvedIngredientName = isGeneric
    ? selectedGeneric?.title || draft.displayName || "-"
    : isOwnProduct
    ? selectedProductUnit?.title || draft.displayName || "-"
    : draft.displayName || "-";

  const { errors } = useFormState({ control, name: `drafts.${index}.percentage` });
  const percentageError = errors?.drafts?.[index]?.percentage?.message;
  const prevErrorRef = useRef<string | null>(null);

  useEffect(() => {
    const message = percentageError ? String(percentageError) : null;
    if (message && message !== prevErrorRef.current) {
      errorView(message);
    }
    prevErrorRef.current = message;
  }, [percentageError]);

  return (
    <div
      className={`tw-rounded-xl tw-border tw-p-4 tw-space-y-3 tw-transition-colors ${
        draft.saved
          ? "tw-bg-primary/5 tw-border-primary/30"
          : "tw-bg-card tw-border-border hover:tw-border-primary/30"
      }`}
    >
      <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-items-start">
        <div className="tw-col-span-3 tw-space-y-1.5">
          <label className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-900">
            Category
            {draft.saved || isExisting ? (
              <Lock className="tw-h-3 tw-w-3 tw-inline-block tw-ml-1 tw-align-text-bottom tw-text-gray-900" />
            ) : null}
          </label>
          {isExisting ? (
            <div className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-flex tw-items-center tw-text-sm tw-font-medium tw-text-gray-900">
              {categoryLabel}
            </div>
          ) : (
            <FormSelect
              control={control}
              name={`drafts.${index}.ingredientType`}
              label={null}
              placeholder="Select type"
              options={ingredientTypeOptions}
              disabled={draft.saved}
              required={!draft.saved}
              errorMessage="Category is required"
              onChange={value => onIngredientTypeChange(index, value)}
              className="!tw-h-9 !tw-text-gray-900"
            />
          )}
        </div>

        <div className="tw-col-span-4 tw-space-y-1.5 tw-min-w-0">
          <label className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-900">
            {ingredientLabel}
          </label>
          {isExisting || draft.saved ? (
            <div className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-flex tw-items-center tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
              {resolvedIngredientName}
            </div>
          ) : isOwnProduct ? (
            <FormSearchSelect
              control={control}
              name={`drafts.${index}.productUnitKey`}
              label=""
              placeholder="Select item"
              options={searchableProductOptions}
              required
              errorMessage="Product and unit size is required"
              noResultsText="No matching product or unit found"
              className="!tw-h-9 !tw-py-0 !tw-text-gray-900"
            />
          ) : isGeneric ? (
            <FormSearchSelect
              control={control}
              name={`drafts.${index}.ingredientId`}
              label=""
              placeholder="Select item"
              options={searchableGenericOptions}
              required
              errorMessage="Ingredient is required"
              noResultsText="Ingredient not found"
              className="!tw-h-9 !tw-py-0 !tw-text-gray-900"
            />
          ) : (
            <div className="tw-h-9 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-muted/30 tw-px-3 tw-flex tw-items-center tw-text-xs tw-text-gray-700">
              Select category first
            </div>
          )}
        </div>

        <div className="tw-col-span-2 tw-space-y-1.5">
          <label className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-900 tw-text-center">
            %
          </label>
          <div className="[&_input]:!tw-h-9 [&_input]:!tw-text-sm">
            <FormInputNumber
              control={control}
              name={`drafts.${index}.percentage`}
              label=""
              placeholder="0"
              disabled={isPercentageDisabled}
              removeDecimal={false}
              enableDecimalWithMinus={false}
              enableLimitedDecimal
              viewDecimalNumber={5}
              onChangeFunction={event => onPercentageChange(index, event.target.value)}
              className="!tw-h-9 !tw-text-sm"
              rules={{
                validate: validatePercentage
              }}
            />
          </div>
        </div>

        <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-end tw-shrink-0">
          <div className="tw-h-[15px]" aria-hidden="true" />
          <div className="tw-mt-1.5">
            <IngredientDraftActions
              saved={draft.saved}
              canSave={isRowSaveable(draft)}
              canRemove={canRemoveRow(draft, pendingCount, hasSaved)}
              isSaving={isSaving}
              onSave={() => onSave(index)}
              onSaveAndAddNew={() => onSaveAndAddNew(index)}
              onRemove={() => onRemove(index)}
            />
          </div>
        </div>
      </div>

      {isGeneric && draft.ingredientId ? (
        <GenericDatasetDescription
          ingredientId={draft.ingredientId}
          description={selectedGeneric?.description ?? null}
          status={selectedGeneric?.status ?? null}
          record={selectedGeneric?.record ?? null}
          allocationMethod={selectedGeneric?.allocationMethod ?? null}
          databaseName={selectedGeneric?.databaseName ?? null}
          open={draft.descriptionOpen}
          onOpenChange={open => onDescriptionOpenChange(index, open)}
        />
      ) : null}
    </div>
  );
};

export default IngredientDraftRow;
